import apiRequest, { authStorage } from './apiClient'

const resource = (basePath) => ({
  list: (params) => apiRequest(basePath, { params }),
  show: (id) => apiRequest(`${basePath}/${id}`),
  create: (payload) => apiRequest(basePath, { method: 'POST', body: payload }),
  update: (id, payload) => apiRequest(`${basePath}/${id}`, { method: 'PUT', body: payload }),
  remove: (id) => apiRequest(`${basePath}/${id}`, { method: 'DELETE' }),
})

export const merchantApi = {
  auth: {
    login: async (payload) => {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: payload,
        auth: false,
      })

      const token = response?.data?.token || response?.token || response?.access_token
      if (token) {
        authStorage.setToken(token)
      }

      return response
    },
    forgotPassword: (payload) => apiRequest('/auth/forgot-password', { method: 'POST', body: payload, auth: false }),
    resetPassword: (payload) => apiRequest('/auth/reset-password', { method: 'POST', body: payload, auth: false }),
    me: () => apiRequest('/auth/me'),
    switchOutlet: (payload) => apiRequest('/auth/default-outlet', { method: 'PATCH', body: payload }),
    logout: async () => {
      try {
        return await apiRequest('/auth/logout', { method: 'POST' })
      } finally {
        authStorage.clearToken("token")
      }
    },
  },

  general: {
    systemInfo: () => apiRequest('/general', { auth: true }),
    permissions: () => apiRequest('/permissions'),
  },

  dashboard: {
    summary: (params) => apiRequest('/dashboard', { params }),
  },

  reports: {
    sales: (params) => apiRequest('/sales-report', { params }),
    export: (params) => apiRequest('/sales-report/export', { params }),
  },

  paymentConfig: {
    configs: (params) => apiRequest('/payment-configurations', { params }),
    updateConfigs: (payload) => apiRequest('/payment-configurations', { method: 'PUT', body: payload }),
  },

  products: {
    ...resource('/products'),
    update: (id, payload) => apiRequest(`/products/${id}`, { method: 'PUT', body: payload }),
    updateImage: (id, file) => {
      const formData = new FormData()
      formData.append('image', file)
      return apiRequest(`/products/${id}/image`, { method: 'POST', body: formData, isFormData: true })
    },
    deleteImage: (id) => apiRequest(`/products/${id}/image`, { method: 'DELETE' }),
  },
  categories: resource('/categories'),
  outlets: {
    list: (params) => apiRequest('/outlets', { params }),
    show: (id) => apiRequest(`/outlets/${id}`),
    create: (payload) => apiRequest('/outlets', { method: 'POST', body: payload }),
    update: (id, payload, isFormData = false) => apiRequest(`/outlets/${id}`, { method: 'PUT', body: payload, isFormData }),
    updateBranding: (id, payload, isFormData = false) => {console.log('etst', id, payload, isFormData); return apiRequest(`/outlets/${id}`, { method: 'POST', body: payload, isFormData })},
    remove: (id) => apiRequest(`/outlets/${id}`, { method: 'DELETE' }),
    syncPos: (id) => apiRequest(`/outlets/${id}/tables/sync`, { method: 'POST' }),
    regenerateQR: (id, tableId) => apiRequest(`/outlets/${id}/tables/${tableId}/qr/regenerate`, { method: 'POST' }),
    downloadQR: (id, tableId) => apiRequest(`/outlets/${id}/tables/${tableId}/qr/image`, { method: 'GET' }),
  },
  kiosks:{
    list: (params) => apiRequest('/kiosks', { params }),
    show: (id) => apiRequest(`/kiosks/${id}`),
    create: (payload) => apiRequest('/kiosks', { method: 'POST', body: payload }),
    update: (id, payload) => apiRequest(`/kiosks/${id}`, { method: 'PUT', body: payload }),
    delete: (id) => apiRequest(`/kiosks/${id}`, { method: 'DELETE' }),
    forceLogout: (id) => apiRequest(`/kiosks/${id}/force-logout`, { method: 'POST' }),
  },
  orders: {
    ...resource('/orders'),
    show: (id) => apiRequest(`/orders/${id}`),
    showSetting: (id) => apiRequest(`/orders/settings`),
    updateStatus: (id, payload) => apiRequest(`/orders/${id}/status`, { method: 'PATCH', body: payload }),
    updateSetting: (payload) => apiRequest(`/orders/settings`, { method: 'PUT', body: payload }),
    markReady: (id, payload) => apiRequest(`/orders/${id}/mark-ready`, { method: 'PATCH', body: payload }),
    complete: (id, payload) => apiRequest(`/orders/${id}/complete`, { method: 'PATCH', body: payload }),
    cancel: (id, payload) => apiRequest(`/orders/${id}/cancel`, { method: 'PATCH', body: payload }),
    revert: (id, payload) => apiRequest(`/orders/${id}/revert`, { method: 'PATCH', body: payload }),
    export: (params) => apiRequest('/orders/export', { params }),
    printReceipt: (id) => apiRequest(`/orders/${id}/receipt`, {
      method: 'GET',
      requestType: 'blob',
      isFormData: true,
      headers: {
        'Accept': 'application/pdf',
        'Content-Type': 'application/pdf',
      },
    }),
  },
  customers: {
    ...resource('/customers'),
    points: (customerId, params) => apiRequest(`/customers/${customerId}/points`, { params }),
    adjustPoints: (customerId, payload) => apiRequest(`/customers/${customerId}/points/adjust`, { method: 'POST', body: payload }),
    export: (params) => apiRequest('/customers/export', { params }),
  },
  outletBanners: {
    list: (params) => apiRequest('/outlet/banners', { params }),
    show: (id) => apiRequest(`/outlet/banners/${id}`),
    create: (payload) => apiRequest('/outlet/banners', { method: 'POST', body: payload }),
    update: (id, payload) => apiRequest(`/outlet/banners/${id}`, { method: 'PUT', body: payload }),
    remove: (id) => apiRequest(`/outlet/banners/${id}`, { method: 'DELETE' }),
    upload: (file) => {
      const formData = new FormData()
      formData.append('file', file)
      return apiRequest('/outlet/banners/upload', { method: 'POST', body: formData, isFormData: true })
    },
    reorder: (payload) => apiRequest('/outlet/banners/reorder', { method: 'POST', body: payload }),
  },
  staff: {
    list: (params) => apiRequest('/staff', { params }),
    export: (params) => apiRequest('/staff/export', { params }),
    show: (id) => apiRequest(`/staff/${id}`),
    update: (id, payload) => apiRequest(`/staff/${id}`, { method: 'PUT', body: payload }),
    updateStatus: (id, payload) => apiRequest(`/staff/${id}/status`, { method: 'PATCH', body: payload }),
    remove: (id) => apiRequest(`/staff/${id}`, { method: 'DELETE' }),
    invite: (payload) => apiRequest('/staff/invitations', { method: 'POST', body: payload }),
    getInvitation: (token) => apiRequest(`/staff/invitations/${token}`),
    acceptInvitation: (token, payload) => apiRequest(`/staff/invitations/${token}/accept`, { method: 'POST', body: payload, auth: false }),
  },
  roles: {
    list: (params) => apiRequest('/staff/roles', { params }),
    create: (payload) => apiRequest('/staff/roles', { method: 'POST', body: payload }),
    show: (id) => apiRequest(`/staff/roles/${id}`),
    update: (id, payload) => apiRequest(`/staff/roles/${id}/permissions`, { method: 'PUT', body: payload }),
  },
  role: {
    update: (id, payload) => apiRequest(`/staff/roles/${id}`, { method: 'PUT', body: payload }),
  },
  profile: {
    update: (payload) => apiRequest('/auth/profile', { method: 'PATCH', body: payload }),
    updatePhoto: (file) => {
      const formData = new FormData();
        formData.append('photo', file);
      return apiRequest('/auth/profile/photo', { method: 'POST', body: formData, isFormData: true })
    },
    removePhoto: () => apiRequest('/auth/profile/photo', { method: 'DELETE' }),
  },
  merchantProfile:{
    show: () => apiRequest('/merchant-profile'),
    update: (payload) => apiRequest('/merchant-profile', { method: 'PATCH', body: payload }),
    updateLogo: (file) => {
      const formData = new FormData();
        formData.append('logo', file);
      return apiRequest('/merchant-profile/logo', { method: 'POST', body: formData, isFormData: true })
    },
    removeLogo: () => apiRequest('/merchant-profile/logo', { method: 'DELETE' }),
  },
  campaignLinks: {
    ...resource('/campaign-links'),
    updateStatus: (id, payload) => apiRequest(`/campaign-links/${id}/status`, { method: 'PATCH', body: payload }),
    analytics: (id, params) => apiRequest(`/campaign-links/${id}/analytics`, { params }),
  },
  vouchers: {
    reward: {
      ...resource('/vouchers/reward-vouchers'),
      updateStatus: (id, payload) => apiRequest(`/vouchers/reward-vouchers/${id}/status`, { method: 'PATCH', body: payload }),
      show: (id) => apiRequest(`/vouchers/reward-vouchers/${id}`),
      update: (id, payload) => apiRequest(`/vouchers/reward-vouchers/${id}`, { method: 'PUT', body: payload }),
    },
    promo: {
      ...resource('/vouchers/promo-codes'),
      updateStatus: (id, payload) => apiRequest(`/vouchers/promo-codes/${id}/status`, { method: 'PATCH', body: payload }),
      show: (id) => apiRequest(`/vouchers/promo-codes/${id}`),
    },
  },
}

export default merchantApi
